import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Optional, Output } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { AuthService } from "@app/core";
import { Brand, ConditionWithLocationIdsDto, Location, LocationWidgetSettings } from "@app/models";
import { AppDemoModalComponent } from "@app/pages/components/widget-settings/modals/app-demo-modal/app-demo-modal.component"; // fixme: imports
import { AppPreviewModalComponent } from "@app/pages/components/widget-settings/modals/app-preview-modal/app-preview-modal.component"; // fixme: imports
import { SmartPageWidgetSettings } from "@app/pages/components/widget-settings/smart-page-widget-settings/smart-page-widget-settings"; // fixme: imports
import { WidgetSettingsModalComponent } from "@app/pages/components/widget-settings/widget-settings-modal/widget-settings-modal.component";
import { ConditionService, WidgetService } from "@app/services";
import { MarketingAppAccountGateService } from "@app/shared/components/advanced-account-setup-dialog";
import { MarketingAppsSettingsService } from "@app/shared/services";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslocoService } from "@ngneat/transloco";
import {
  CreateCustomWidgetPostDto,
  LocationWidgetSettingsGetDto,
  LocationWidgetSettingsMode,
  LocationWithDefaultWidgetGetDto
} from "clearline-api";
import { isEqualFlatObjects, WidgetType } from "clearline-common";
import { RoleTypes } from "core-kit";
import { Subject } from "rxjs";
import { filter, take, takeUntil } from "rxjs/operators";
import { ConfirmDialogComponent, ConfirmDialogOptions } from "ui-kit";

@Component({
  selector: "app-widgets-form-control",
  templateUrl: "./widgets-form-control.component.html",
  styleUrls: ["./widgets-form-control.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WidgetsFormControlComponent implements OnInit, OnDestroy {
  constructor(
    @Optional() private mappService: MarketingAppsSettingsService,
    private widgetSvc: WidgetService, // todo: move usage of service from HTML to component
    private modalSvc: NgbModal,
    private dialog: MatDialog,
    private translateSvc: TranslocoService,
    private authService: AuthService,
    private conditionService: ConditionService,
    private marketingAppAccountGate: MarketingAppAccountGateService
  ) {}

  private _widgets: LocationWithDefaultWidgetGetDto[] = [];
  private unsubscribe$ = new Subject<void>();

  @Input() showHeader = true;
  @Input() isSingleLocationAccount = true;
  @Input() location: Location;
  @Input() brand: Brand;
  @Input() selectedWidgetId: number;

  @Input() widgetsInThePlan: LocationWithDefaultWidgetGetDto[];
  @Input() locationWidgetMode: LocationWidgetSettingsMode = LocationWidgetSettingsMode.Cmc;

  @Input() set widgets(widgets: LocationWithDefaultWidgetGetDto[]) {
    if (widgets && !isEqualFlatObjects(widgets, this._widgets)) {
      this._widgets = widgets;
    }
  }

  @Output() createCustomApp = new EventEmitter<CreateCustomWidgetPostDto>();
  @Output() deleteCustomApp = new EventEmitter<LocationWithDefaultWidgetGetDto>();
  @Output() updatedOneWidget = new EventEmitter<LocationWithDefaultWidgetGetDto>();

  get widgets(): Array<LocationWithDefaultWidgetGetDto> {
    return this._widgets;
  }

  get isAdmin() {
    return this.authService.requiredMinimumRole(RoleTypes.PartnerAdmin);
  }

  ngOnInit(): void {
    this.mappService?.createCustomAppAction$
      .pipe(
        filter((v) => !!v),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((widget: LocationWithDefaultWidgetGetDto) => {
        this.openSettings(widget, true);
      });
  }

  openSettings(widget: LocationWithDefaultWidgetGetDto, isCreateCustomApp = false) {
    const largeModalWidgets = [WidgetType.IssueCoupon, WidgetType.SmartPage];
    const classSize = largeModalWidgets.includes(widget.locationWidgetSettings.defaultWidgetSettingsId) ? "xxl" : "lg";
    const featureModal = this.modalSvc.open(WidgetSettingsModalComponent, { backdrop: "static", size: classSize, centered: true });
    const widgetId = widget?.widget?.id;
    const locationWidget = { ...widget.locationWidgetSettings };
    const isCustomMapp = locationWidget.defaultWidgetSettingsId !== widgetId;
    const widgetSettings = JSON.parse(locationWidget.widgetSettingsJson || "{}");
    const widgetName = widget.widget.name;

    const comp: WidgetSettingsModalComponent = featureModal.componentInstance;
    comp.locationWidget = locationWidget;
    comp.title = isCreateCustomApp ? this.translateSvc.translate("widgets.common.createWidget", { name: widgetName }) : widgetName;
    comp.widgetId = widgetId;
    // todo: refactor modal component -> remove redundant properties
    comp.isCustomMapp = isCustomMapp;
    comp.baseWidgetType = locationWidget.defaultWidgetSettingsId;
    comp.deliveryMethod = locationWidget.deliveryMethod;
    comp.configured = locationWidget.configured;
    comp.useForAllLocations = locationWidget.useForAllLocations;
    comp.location = this.location;
    comp.canApplySettingsToAllLocations = !this.isSingleLocationAccount;
    comp.brand = this.brand;
    comp.createMode = false;
    comp.widgetsInThePlan = this.widgetsInThePlan;
    comp.widgetSettingsJson = isCustomMapp ? JSON.stringify({ ...widgetSettings, appName: widgetName }) : JSON.stringify(widgetSettings);

    featureModal.result
      .then((wSettings: LocationWidgetSettingsGetDto) => {
        if (isCreateCustomApp && !!this.mappService) {
          const customAppPayload = this.mappService.generateCustomAppPayload(widget, wSettings);
          this.createCustomApp.emit(customAppPayload);
        } else {
          const { widgetSettingsJson, deliveryMethod, configured, useForAllLocations } = wSettings;
          const locationWidgetSettings: LocationWidgetSettingsGetDto = {
            ...widget.locationWidgetSettings,
            configured,
            deliveryMethod,
            widgetSettingsJson,
            useForAllLocations
          };
          const updatedWidget: LocationWithDefaultWidgetGetDto = {
            ...widget,
            locationWidgetSettings
          };

          this.handleWidgetChangeOnResult(updatedWidget);
        }
      })
      .catch(() => {});
  }

  onWidgetChange(widget: LocationWithDefaultWidgetGetDto) {
    if (widget.locationWidgetSettings.disabled) {
      this.handleDisabledWidget(widget);
    } else {
      this.handleWidgetChangeOnResult(widget);
    }
  }

  onWidgetSettingsClick(widgetDto: LocationWithDefaultWidgetGetDto): void {
    const hasWidgetCampaign = this.widgetSvc.hasWidgetCampaign(widgetDto.locationWidgetSettings.defaultWidgetSettingsId);

    if (!hasWidgetCampaign) {
      return;
    }

    const { configured, widgetSettingsJson, defaultWidgetSettingsId } = widgetDto.locationWidgetSettings;

    if (configured && !!widgetSettingsJson) {
      this.openSettings(widgetDto);
      return;
    }

    this.marketingAppAccountGate.open({
      widgetType: defaultWidgetSettingsId,
      name: widgetDto.widget.name,
      iconUrl: widgetDto.icon,
      open: () => this.openSettings(widgetDto)
    });
  }

  onWidgetDemoClick(widgetDto: LocationWithDefaultWidgetGetDto): void {
    this.viewDemoSettings(widgetDto);
  }

  onWidgetDeleteClick(widgetDto: LocationWithDefaultWidgetGetDto): void {
    const { name } = widgetDto.widget;
    const message = this.translateSvc.translate(`widgets.common.deleteConfirmMessage`, { name });
    const description = this.translateSvc.translate(`widgets.common.deleteConfirmDescription`);
    const options: ConfirmDialogOptions = {
      title: this.translateSvc.translate(`widgets.common.deleteConfirmTitle`),
      description: `${message}\n\n${description}`,
      cancelBtnLabel: this.translateSvc.translate(`common.controls.cancel`),
      confirmBtnLabel: this.translateSvc.translate(`common.controls.confirm`)
    };

    this.dialog
      .open(ConfirmDialogComponent, {
        panelClass: "modal-standard-s",
        disableClose: true,
        data: options
      })
      .afterClosed()
      .subscribe((isConfirm: boolean) => {
        if (isConfirm) {
          this.deleteCustomApp.emit(widgetDto);
        }
      });
  }

  onWidgetPreviewClick(widgetDto: LocationWithDefaultWidgetGetDto): void {
    this.viewAppSettings(widgetDto);
  }

  private handleDisabledWidget(widget: LocationWithDefaultWidgetGetDto): void {
    const locWidget: LocationWidgetSettings = widget.locationWidgetSettings as LocationWidgetSettings; // todo: align types

    this.updateSmartWidgetApps(locWidget);
    this.conditionService
      .getWidgetConditions(widget.widget.id)
      .pipe(take(1))
      .subscribe((info: ConditionWithLocationIdsDto[] = []) => {
        const hasConditionOnLocation: boolean = info?.some((item: ConditionWithLocationIdsDto) => {
          return item.locationIds.some((locationId: number) => locationId === this.location.id);
        });

        if (hasConditionOnLocation || this.getDeactiveSMSCampaignByWidgets(locWidget)) {
          const confirmMessage: string = this.translateSvc.translateObject("widgets.common.disableConfirmMessage");
          const skipConfirmation = this.locationWidgetMode === LocationWidgetSettingsMode.Cfs;

          if (!skipConfirmation) {
            locWidget;
          }

          if (locWidget.disabled) {
            this.handleWidgetChangeOnResult(widget);
          }
        } else {
          this.handleWidgetChangeOnResult(widget);
        }
      });
  }

  getDeactiveSMSCampaignByWidgets(widget: LocationWidgetSettings) {
    return this.widgetSvc.getDeactiveSMSCampaignSettings(widget.defaultWidgetSettingsId, widget);
  }

  updateSmartWidgetApps(widget: LocationWidgetSettings) {
    if (!widget.disabled || widget.defaultWidgetSettingsId === WidgetType.SmartPage) return;
    const smartWidget = this._widgets.find((w) => w.widget.id === WidgetType.SmartPage);

    if (smartWidget?.locationWidgetSettings && smartWidget.locationWidgetSettings.widgetSettingsJson) {
      const settings: SmartPageWidgetSettings = JSON.parse(smartWidget.locationWidgetSettings.widgetSettingsJson);

      if (settings?.marketingActions?.length) {
        settings.marketingActions = settings.marketingActions.map((app) => {
          if (app.widgetId === widget.defaultWidgetSettingsId) {
            app.enabled = false;
          }
          return app;
        });

        smartWidget.locationWidgetSettings.widgetSettingsJson = JSON.stringify(settings);
      }
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private handleWidgetChangeOnResult(widget: LocationWithDefaultWidgetGetDto): void {
    this.updatedOneWidget.emit(widget);
  }

  private viewAppSettings(widgetDto: LocationWithDefaultWidgetGetDto) {
    const widget = widgetDto.widget;
    const featureModal = this.modalSvc.open(AppPreviewModalComponent, { backdrop: "static", size: "lg", centered: true });
    const comp: AppPreviewModalComponent = featureModal.componentInstance;
    comp.title = widget?.name;
    comp.widgetId = widget.id;
    comp.locationId = this.location.id;
    comp.settings = this.widgetSvc.getQrSettings(widget?.id, widgetDto?.locationWidgetSettings as LocationWidgetSettings); // todo: align types
  }

  private viewDemoSettings(widgetDto: LocationWithDefaultWidgetGetDto) {
    const widget = widgetDto.widget;
    const featureModal = this.modalSvc.open(AppDemoModalComponent, { backdrop: "static", size: "lg", centered: true });
    const comp: AppDemoModalComponent = featureModal.componentInstance;
    comp.title = widget.name;
    comp.description = widget.description;
    comp.videoId = widgetDto.defaultWidgetSettings.videoId;
  }
}
